import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { Dropdown } from 'primereact/dropdown';
import { FilterMatchMode } from 'primereact/api';
import moment from 'moment';
import {
    fetchTaxInvoices,
    fetchTaxInvoiceBuyers,
    fetchTaxInvoiceSellers,
    fetchDefaultTaxInvoiceSeller,
    selectFilteredInvoices,
    selectInvoices,
    selectTaxInvoiceBuyers,
    selectTaxInvoiceSellers,
    selectDefaultSeller,
    selectLoading,
    selectError,
    selectStatusFilter,
    setStatusFilter
} from '../store/slices/taxInvoiceSlice';
import { saveTaxInvoice, deleteTaxInvoice, getTaxInvoiceById, getNextTaxInvoiceNo } from '../utilities/api';
import TaxInvoiceModal from './TaxInvoiceModal';
import TaxInvoicePreviewDialog from './TaxInvoicePreviewDialog';
import '../masters/CategoryMaster.css';

const STATUS_SEVERITY = {
    Draft: 'secondary',
    Sent: 'info',
    Paid: 'success',
    Overdue: 'danger',
    Cancelled: 'danger'
};

const STATUS_FILTER_OPTIONS = [
    { label: 'All Statuses', value: 'all' },
    { label: 'Draft', value: 'Draft' },
    { label: 'Sent', value: 'Sent' },
    { label: 'Paid', value: 'Paid' },
    { label: 'Overdue', value: 'Overdue' },
    { label: 'Cancelled', value: 'Cancelled' }
];

const TaxInvoiceContent = () => {
    const dispatch = useDispatch();
    const toast = useRef(null);

    const filteredInvoices = useSelector(selectFilteredInvoices);
    const invoices = useSelector(selectInvoices);
    const buyers = useSelector(selectTaxInvoiceBuyers);
    const sellers = useSelector(selectTaxInvoiceSellers);
    const defaultSeller = useSelector(selectDefaultSeller);
    const loading = useSelector(selectLoading);
    const error = useSelector(selectError);
    const statusFilter = useSelector(selectStatusFilter);

    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({ global: { value: null, matchMode: FilterMatchMode.CONTAINS } });
    const [displayDialog, setDisplayDialog] = useState(false);
    const [selectedInvoice, setSelectedInvoice] = useState(null);
    const [nextInvoiceNo, setNextInvoiceNo] = useState('');
    const [previewInvoice, setPreviewInvoice] = useState(null);
    const [previewVisible, setPreviewVisible] = useState(false);

    useEffect(() => {
        dispatch(fetchTaxInvoices());
        dispatch(fetchTaxInvoiceBuyers());
        dispatch(fetchTaxInvoiceSellers());
        dispatch(fetchDefaultTaxInvoiceSeller());
    }, [dispatch]);

    useEffect(() => {
        if (error) toast.current?.show({ severity: 'error', summary: 'Load Error', detail: error, life: 5000 });
    }, [error]);

    const totals = useMemo(() => {
        const paid = invoices.filter((i) => i.status === 'Paid').reduce((s, i) => s + Number(i.grand_total || 0), 0);
        const outstanding = invoices.filter((i) => ['Sent', 'Overdue'].includes(i.status)).reduce((s, i) => s + Number(i.grand_total || 0), 0);
        return { count: invoices.length, paid, outstanding };
    }, [invoices]);

    const reload = () => dispatch(fetchTaxInvoices());

    const openAdd = async () => {
        try {
            const res = await getNextTaxInvoiceNo();
            setNextInvoiceNo(res?.invoice_no || '');
        } catch {
            setNextInvoiceNo('');
        }
        setSelectedInvoice(null);
        setDisplayDialog(true);
    };

    const openEdit = async (row) => {
        try {
            const res = await getTaxInvoiceById(row.invoice_id);
            setSelectedInvoice(res?.data || row);
        } catch {
            setSelectedInvoice(row);
        }
        setDisplayDialog(true);
    };

    const openPreview = async (row) => {
        try {
            const res = await getTaxInvoiceById(row.invoice_id);
            setPreviewInvoice(res?.data || row);
        } catch {
            setPreviewInvoice(row);
        }
        setPreviewVisible(true);
    };

    const handleSave = async (payload) => {
        try {
            const resData = await saveTaxInvoice(payload);
            if (resData?.status) {
                toast.current.show({ severity: 'success', summary: 'Success', detail: resData.message || 'Saved successfully', life: 3000 });
                setDisplayDialog(false);
                setSelectedInvoice(null);
                reload();
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: resData?.message || 'Save failed', life: 4000 });
            }
        } catch (err) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: err?.response?.data?.message || err?.message || 'Save failed', life: 4000 });
        }
    };

    const handleDelete = (row) => {
        confirmDialog({
            message: `Delete invoice "${row.invoice_no}"? This cannot be undone.`,
            header: 'Confirm Delete',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Yes, Delete',
            rejectLabel: 'Cancel',
            acceptClassName: 'p-button-danger',
            rejectClassName: 'p-button-text',
            accept: async () => {
                try {
                    const res = await deleteTaxInvoice(row.invoice_id);
                    toast.current.show({ severity: res?.status ? 'success' : 'error', summary: res?.status ? 'Deleted' : 'Error', detail: res?.message, life: 3000 });
                    reload();
                } catch (err) {
                    toast.current.show({ severity: 'error', summary: 'Error', detail: err?.message || 'Delete failed', life: 4000 });
                }
            }
        });
    };

    const dateBody = (row) => (row.invoice_date ? moment(row.invoice_date).format('DD-MMM-YYYY') : '');
    const amountBody = (row) => Number(row.grand_total || 0).toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 });
    const statusBody = (row) => <Tag value={row.status} severity={STATUS_SEVERITY[row.status] || 'secondary'} rounded style={{ minWidth: '80px', textAlign: 'center' }} />;

    const actionBody = (row) => (
        <div className="flex gap-1 justify-content-center">
            <Button icon="pi pi-eye" rounded text className="p-button-info" tooltip="View / Print / PDF" tooltipOptions={{ position: 'top' }} onClick={() => openPreview(row)} />
            <Button icon="pi pi-pencil" rounded text className="p-button-warning" tooltip="Edit" tooltipOptions={{ position: 'top' }} onClick={() => openEdit(row)} />
            <Button icon="pi pi-trash" rounded text className="p-button-danger" tooltip="Delete" tooltipOptions={{ position: 'top' }} onClick={() => handleDelete(row)} />
        </div>
    );

    return (
        <Card className="bill-reports-container">
            <Toast ref={toast} />
            <ConfirmDialog />

            <div className="flex flex-wrap gap-3 mb-4">
                <div className="flex-1" style={{ minWidth: '160px', background: '#f8fafc', borderRadius: 10, padding: '0.9rem 1.1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Total Invoices</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#1e293b' }}>{totals.count}</div>
                </div>
                <div className="flex-1" style={{ minWidth: '160px', background: '#f0fdf4', borderRadius: 10, padding: '0.9rem 1.1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#16a34a', fontWeight: 600, textTransform: 'uppercase' }}>Collected</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#16a34a' }}>{totals.paid.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</div>
                </div>
                <div className="flex-1" style={{ minWidth: '160px', background: '#fef2f2', borderRadius: 10, padding: '0.9rem 1.1rem' }}>
                    <div style={{ fontSize: '0.75rem', color: '#dc2626', fontWeight: 600, textTransform: 'uppercase' }}>Outstanding</div>
                    <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#dc2626' }}>{totals.outstanding.toLocaleString('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 })}</div>
                </div>
            </div>

            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                <div className="flex align-items-center gap-2 flex-wrap">
                    <h3 className="text-xl font-semibold m-0">Tax Invoices</h3>
                    <Dropdown value={statusFilter} options={STATUS_FILTER_OPTIONS} onChange={(e) => dispatch(setStatusFilter(e.value))} style={{ minWidth: '160px' }} />
                </div>

                <div className="flex flex-wrap gap-2 align-items-center">
                    <span className="p-input-icon-left" style={{ minWidth: '220px' }}>
                        <i className="pi pi-search" />
                        <InputText
                            value={globalFilterValue}
                            onChange={(e) => {
                                setGlobalFilterValue(e.target.value);
                                setFilters({ global: { value: e.target.value, matchMode: FilterMatchMode.CONTAINS } });
                            }}
                            placeholder="Search invoice no, buyer, seller..."
                            className="w-full"
                        />
                    </span>
                    <Button label="New Invoice" icon="pi pi-plus" className="p-button-primary" onClick={openAdd} />
                </div>
            </div>

            {loading ? (
                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                    <ProgressSpinner />
                </div>
            ) : (
                <div className="sub-masters-table-wrap">
                    <DataTable
                        value={filteredInvoices}
                        filters={filters}
                        globalFilterFields={['invoice_no', 'buyer_name', 'seller_name']}
                        paginator
                        rows={10}
                        rowsPerPageOptions={[10, 25, 50]}
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                        currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                        showGridlines
                        tableStyle={{ minWidth: '60rem' }}
                        className="p-datatable-sm sub-masters-table"
                        emptyMessage="No invoices found"
                    >
                        <Column field="invoice_no" header="Invoice No" sortable style={{ width: '140px' }} />
                        <Column field="invoice_date" header="Date" body={dateBody} sortable style={{ width: '130px' }} />
                        <Column field="seller_name" header="Seller" sortable />
                        <Column field="buyer_name" header="Billed To" sortable />
                        <Column field="grand_total" header="Amount" body={amountBody} sortable style={{ width: '140px' }} />
                        <Column field="status" header="Status" body={statusBody} sortable style={{ width: '120px', textAlign: 'center' }} />
                        <Column header="Actions" body={actionBody} style={{ width: '140px', textAlign: 'center' }} />
                    </DataTable>
                </div>
            )}

            {displayDialog && (
                <TaxInvoiceModal
                    visible={displayDialog}
                    onHide={() => setDisplayDialog(false)}
                    invoice={selectedInvoice}
                    onSave={handleSave}
                    buyers={buyers}
                    sellers={sellers}
                    defaultSeller={defaultSeller}
                    nextInvoiceNo={nextInvoiceNo}
                />
            )}

            <TaxInvoicePreviewDialog visible={previewVisible} onHide={() => setPreviewVisible(false)} invoice={previewInvoice} defaultSeller={defaultSeller} />
        </Card>
    );
};

export default TaxInvoiceContent;
